let storageKey: string;
let setEvent: string;
let preInitFailure = true;

interface ICrossStorageOptions<StorageChange> {
  /**
   * An override string to configure the field in sessionStorage CrossStorage occupies
   */
  storageKey?: string;

  /**
   * An override string to configure the field in localStorage used to sync changes between tabs
   */
  setEvent?: string;

  /**
   * An override string to configure the field in localStorage used to request the current
   * version of CrossStorage
   */
  getEvent?: string;

  /**
   * An override string to configure the field in localStorage used to respond to requests
   * for the current version of CrossStorage
   */
  restoreEvent?: string;

  /**
   * A function called whenever this tab receives an updated version of CrossStorage
   * from another tab via a setEvent
   */
  onStorageChange?: (nextStorage: StorageChange, prevStorage: StorageChange) => void;
}

interface ICrossStorage<StorageKeys extends string, ValueConstraint> {
  /**
   * Setup function that should be run before performing any CrossStorage operations.
   * Is called automatically but can be called again to overwrite previous values.
   */
  init: (options?: ICrossStorageOptions<{ [K in StorageKeys]: ValueConstraint }>) => void;

  /**
   * Returns a value from CrossStorage. Returns undefined if it does not exist.
   */
  getItem: (key: StorageKeys) => ValueConstraint;

  /**
   * Sets item in CrossStorage
   */
  setItem: (key: StorageKeys, value?: ValueConstraint) => void;

  /**
   * Removes a field from CrossStorage
   */
  removeItem: (key: StorageKeys) => void;

  /**
   * Resets session to an empty object
   */
  clear: () => void;
}

function logError(methodName: string) {
  if (console) {
    // eslint-disable-next-line no-console
    console.error(`\`crossTabStorage.${methodName}\` called before full initialization!`);
  }
}

function getStorage() {
  const storage = sessionStorage.getItem(storageKey);
  return storage ? JSON.parse(storage) : undefined;
}

function setStorage(storage: Record<string, unknown>) {
  sessionStorage.setItem(storageKey, JSON.stringify(storage));
  localStorage.setItem(setEvent, JSON.stringify(storage));
  localStorage.removeItem(setEvent);
}

// istanbul ignore next
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const getCrossStorage = <StorageKeys extends string, ValueConstraint>(
  initOptions?: ICrossStorageOptions<{ [K in StorageKeys]: ValueConstraint }>
): ICrossStorage<StorageKeys, ValueConstraint> => {
  const crossStorage: ICrossStorage<StorageKeys, ValueConstraint> = {
    init: ({
      getEvent = 'getCrossTabStorage',
      onStorageChange = noop,
      restoreEvent = 'restoreCrossTabStorage',
      setEvent: setEventIn = 'setCrossTabStorage',
      storageKey: storageKeyIn = 'crossTabStorage',
    }: ICrossStorageOptions<{ [K in StorageKeys]: ValueConstraint }> = {}) => {
      storageKey = storageKeyIn;
      setEvent = setEventIn;

      const loadFromExistingTab = (event: StorageEvent) => {
        if (event.key === restoreEvent && event.newValue) {
          sessionStorage.setItem(storageKey, event.newValue);
        }
      };

      if (!getStorage()) {
        // creates a listener for other tabs sending their shared storage
        window.addEventListener('storage', loadFromExistingTab);

        // fires an event for other tabs to know we're asking for their shared storage
        localStorage.setItem(getEvent, `${Date.now()}`);

        // wait a tick, and then remove the event listener to prevent mixing with other new tabs
        setTimeout(() => {
          window.removeEventListener('storage', loadFromExistingTab);

          preInitFailure = false;
          // 10ms timeout is on purpose. During testing it was seen that it would take between 2-3ms
          // for tabs to sync and without this we risk race conditions.
        }, 10);
      } else {
        preInitFailure = false;
      }

      // start supporting other tabs updating data or requesting ours
      window.addEventListener('storage', (event) => {
        // send data to a requesting tab
        if (event.key === getEvent) {
          localStorage.setItem(restoreEvent, sessionStorage.getItem(storageKey) || '');
          localStorage.removeItem(restoreEvent);
        }

        // read updates from an updating tab
        if (event.key === setEvent && event.newValue) {
          const prevStorage = getStorage() || {};
          const nextStorage = JSON.parse(event.newValue) || {};

          sessionStorage.setItem(storageKey, event.newValue);

          onStorageChange(nextStorage, prevStorage);
        }
      });
    },

    getItem: (key) => {
      if (preInitFailure) {
        logError('getItem');
      }

      return (getStorage() || {})[key];
    },

    setItem: (key, value) => {
      if (preInitFailure) {
        logError('setItem');
      }

      const sharedStorage = getStorage() || {};
      sharedStorage[key] = value;
      setStorage(sharedStorage);
    },

    removeItem: (key) => {
      if (preInitFailure) {
        logError('removeItem');
      }

      const sharedStorage = getStorage() || {};
      delete sharedStorage[key];
      setStorage(sharedStorage);
    },

    clear: () => {
      if (preInitFailure) {
        logError('clear');
      }

      setStorage({});
    },
  };

  crossStorage.init(initOptions);

  return crossStorage;
};

export default getCrossStorage;
